import React from "react";
import { Button, Dropdown, Tooltip } from "antd";
import { DownOutlined } from "@ant-design/icons";
import FormBuilderValidateOnSubmitButton from "./FormBuilderValidateOnSubmitButton";
import CustomPopconfirm from "../Popover/CustomPopconfirm";

interface MainAction {
	label: string;
	onClick: () => void;
	type?: "primary" | "default" | "dashed" | "text" | "link";
	icon?: React.ReactNode;
	loading?: boolean;
	disabled?: boolean;
	tooltip?: string;
	confirmTitle?: React.ReactNode;
	onValidationError?: (error: unknown) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	form?: any;
}

interface DropdownItem {
	key: string;
	label: string;
	icon?: React.ReactNode;
	onClick: () => void;
	disabled?: boolean;
	danger?: boolean;
}

interface CustomDropdownButtonProps {
	mainAction: MainAction;
	dropdownItems: DropdownItem[];
	dropdownPlacement?: "bottomLeft" | "bottomRight";
	hideDropdown?: boolean;
}

const CustomDropdownButton: React.FC<CustomDropdownButtonProps> = ({
	mainAction,
	dropdownItems,
	dropdownPlacement = "bottomRight",
	hideDropdown = false,
}) => {
	const {
		label,
		onClick,
		type = "primary",
		icon,
		loading = false,
		disabled = false,
		tooltip,
		confirmTitle,
		onValidationError,
		form,
	} = mainAction;

	const isMainDisabled = disabled || loading;

	const renderMainButton = () => {
		let button;

		if (form && !disabled) {
			button = (
				<FormBuilderValidateOnSubmitButton
					form={form}
					labelText={label}
					isLoading={loading}
					handleFormSave={onClick}
					confirmTitle={confirmTitle}
					onValidationError={onValidationError}
					icon={icon}
					type={type}
				/>
			);
		} else {
			button = (
				<Button
					type={type}
					icon={icon}
					loading={loading}
					disabled={isMainDisabled}
					onClick={onClick}
				>
					{label}
				</Button>
			);

			if (confirmTitle) {
				button = (
					<CustomPopconfirm title={confirmTitle} handleConfirm={onClick}>
						{button}
					</CustomPopconfirm>
				);
			}
		}

		if (tooltip) {
			button = <Tooltip title={tooltip}>{button}</Tooltip>;
		}

		return button;
	};

	if (hideDropdown) {
		return <>{renderMainButton()}</>;
	}

	const menuItems = dropdownItems.map((item) => ({
		key: item.key,
		label: item.label,
		icon: item.icon,
		onClick: item.onClick,
		disabled: item.disabled,
		danger: item.danger,
	}));

	return (
		<Button.Group>
			{renderMainButton()}
			<Dropdown
				menu={{ items: menuItems }}
				placement={dropdownPlacement}
				disabled={loading}
			>
				<Button type={type} icon={<DownOutlined />} disabled={loading} />
			</Dropdown>
		</Button.Group>
	);
};

export default CustomDropdownButton;
